import { useState, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { clients, memories, coachingNotes, workoutLogs, clientStats, assignments, clientRecovery, billing, clientDirectives, directives, clientConversations } from '../../api/client'
import MediaGallery from '../../components/MediaGallery'
import PageHeader from '../../components/PageHeader'
import { PageLayout, Badge, StatusBadge, Card, ActionCard, ListCard, ListCardItem, Skeleton } from '../../components/ui'
import { formatDate } from '../../utils/date'
import { memoryTypes, getChangeRating } from '../../utils/constants'

// Get the WebObjects base path for constructing photo URLs
function getBasePath() {
  const path = window.location.pathname
  const match = path.match(/^(.*\/WebObjects\/forge\.woa)/)
  return match ? match[1] : ''
}

function QuickActionButton({ icon, label, onClick, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-status-info/20 text-status-info',
    green: 'bg-status-success/20 text-status-success',
    purple: 'bg-purple-500/20 text-purple-400',
    teal: 'bg-lume-teal/20 text-lume-teal',
    orange: 'bg-orange-500/20 text-orange-400',
  }

  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-2 p-4 bg-dial-surface border border-dial-border rounded-xl hover:bg-dial-elevated transition-colors"
    >
      <div className={`w-12 h-12 rounded-full ${colorClasses[color]} flex items-center justify-center`}>
        {icon}
      </div>
      <span className="text-sm font-medium text-indices-medium">{label}</span>
    </button>
  )
}

function QuickActionLink({ to, icon, label, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-status-info/20 text-status-info',
    green: 'bg-status-success/20 text-status-success',
    purple: 'bg-purple-500/20 text-purple-400',
    teal: 'bg-lume-teal/20 text-lume-teal',
    orange: 'bg-orange-500/20 text-orange-400',
  }

  return (
    <Link
      to={to}
      className="flex flex-col items-center gap-2 p-4 bg-dial-surface border border-dial-border rounded-xl hover:bg-dial-elevated transition-colors"
    >
      <div className={`w-12 h-12 rounded-full ${colorClasses[color]} flex items-center justify-center`}>
        {icon}
      </div>
      <span className="text-sm font-medium text-indices-medium">{label}</span>
    </Link>
  )
}

function PortalLinkModal({ isOpen, onClose, client, onGenerate, link, isGenerating, onSendInvite, inviteStatus }) {
  if (!isOpen) return null

  const handleCopy = async () => {
    if (link) {
      await navigator.clipboard.writeText(link)
      alert('Link copied to clipboard!')
    }
  }

  const hasEmail = client.email && client.email.trim()
  const hasPhone = client.phone && client.phone.trim()

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-dial-surface border border-dial-border rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-dial-border flex items-center justify-between">
          <h2 className="font-semibold text-indices-bright font-display">Send Portal Invite</h2>
          <button onClick={onClose} className="text-indices-muted hover:text-indices-bright transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-sm text-indices-medium">
            Send a login link to <strong className="text-indices-bright">{client.firstName} {client.lastName}</strong> to access their client portal.
          </p>

          {/* Send invite buttons */}
          <div className="space-y-2">
            {inviteStatus && (
              <div className={`text-sm text-center py-2 px-3 rounded-lg ${inviteStatus.success ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                {inviteStatus.message}
              </div>
            )}

            <button
              onClick={() => onSendInvite('email')}
              disabled={!hasEmail || inviteStatus?.sending === 'email'}
              className="w-full py-3 bg-lume-teal text-dial-black rounded-xl font-medium hover:bg-lume-green hover:shadow-lume disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {inviteStatus?.sending === 'email' ? 'Sending...' : `Send via Email${hasEmail ? ` (${client.email})` : ''}`}
            </button>

            <button
              onClick={() => onSendInvite('sms')}
              disabled={!hasPhone || inviteStatus?.sending === 'sms'}
              className="w-full py-3 bg-dial-elevated text-indices-bright border border-dial-border rounded-xl font-medium hover:bg-dial-surface disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              {inviteStatus?.sending === 'sms' ? 'Sending...' : `Send via SMS${hasPhone ? ` (${client.phone})` : ' (no phone)'}`}
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-dial-border" />
            <span className="text-xs text-indices-muted font-mono tracking-wider">OR COPY LINK</span>
            <div className="flex-1 h-px bg-dial-border" />
          </div>

          {/* Manual link generation */}
          {!link ? (
            <button
              onClick={onGenerate}
              disabled={isGenerating}
              className="w-full py-2.5 bg-dial-elevated text-indices-medium border border-dial-border rounded-xl font-medium hover:bg-dial-surface disabled:opacity-50 transition-all"
            >
              {isGenerating ? 'Generating...' : 'Generate Manual Link'}
            </button>
          ) : (
            <div className="space-y-3">
              <div className="bg-dial-elevated border border-dial-border rounded-lg p-3">
                <p className="text-xs text-indices-muted mb-1">Portal Link</p>
                <p className="text-sm font-mono text-indices-bright break-all">{link}</p>
              </div>
              <button
                onClick={handleCopy}
                className="w-full py-2.5 bg-lume-teal text-dial-black rounded-lg font-medium hover:bg-lume-green transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                Copy Link
              </button>
              <p className="text-xs text-indices-muted text-center">
                This link expires in 15 minutes
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function QuickMessageModal({ isOpen, onClose, client }) {
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState('idle') // idle | loading | success | error | no_conversation
  const [errorMsg, setErrorMsg] = useState('')

  if (!isOpen) return null

  const clientName = `${client.firstName || ''} ${client.lastName || ''}`.trim()

  const handleSend = async () => {
    if (!message.trim()) return
    setStatus('loading')
    setErrorMsg('')

    try {
      const convData = await clientConversations.list(client.id)
      const conversations = convData?.conversations || convData || []

      if (!conversations.length) {
        setStatus('no_conversation')
        return
      }

      // Find most recent active conversation (first in list, typically sorted by recency)
      const activeConv = conversations.find(c => c.isActive !== false) || conversations[0]

      await clientConversations.reply(activeConv.id, message.trim())
      setStatus('success')
      setMessage('')

      // Auto-close after brief success feedback
      setTimeout(() => {
        setStatus('idle')
        onClose()
      }, 1500)
    } catch (e) {
      setStatus('error')
      setErrorMsg(e.message || 'Failed to send message')
    }
  }

  const handleClose = () => {
    setMessage('')
    setStatus('idle')
    setErrorMsg('')
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={handleClose}>
      <div className="bg-dial-surface border border-dial-border rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-dial-border flex items-center justify-between">
          <h2 className="font-semibold text-indices-bright font-display">Send Message</h2>
          <button onClick={handleClose} className="text-indices-muted hover:text-indices-bright transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-sm text-indices-medium">
            Send a message to <strong className="text-indices-bright">{clientName}</strong>
          </p>

          {status === 'no_conversation' ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-dial-elevated border border-dial-border rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-indices-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-sm text-indices-muted">No active conversation</p>
              <p className="text-xs text-indices-recessed mt-1">{clientName} needs to start a chat first.</p>
              <button
                onClick={handleClose}
                className="mt-4 px-4 py-2 text-sm text-indices-muted hover:text-indices-bright transition-colors"
              >
                Close
              </button>
            </div>
          ) : status === 'success' ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-lume-teal/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-lume-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-lume-teal font-medium">Message sent</p>
            </div>
          ) : (
            <>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Type your message..."
                rows={4}
                disabled={status === 'loading'}
                className="w-full px-3 py-2 text-sm bg-dial-elevated border border-dial-border rounded-lg text-indices-bright placeholder-indices-muted focus:border-lume-teal focus:outline-none resize-none disabled:opacity-50"
                autoFocus
              />

              {status === 'error' && (
                <div className="text-sm text-center py-2 px-3 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20">
                  {errorMsg || 'Failed to send message'}
                </div>
              )}

              <p className="text-xs text-indices-muted">
                Message will appear in {clientName}'s AI chat
              </p>

              <div className="flex gap-2">
                <button
                  onClick={handleClose}
                  className="flex-1 py-2.5 text-sm text-indices-muted hover:text-indices-bright transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSend}
                  disabled={status === 'loading' || !message.trim()}
                  className="flex-1 py-2.5 bg-lume-teal text-dial-black rounded-xl font-semibold text-sm hover:bg-lume-green hover:shadow-lume disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                >
                  {status === 'loading' ? (
                    'Sending...'
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// Get change rating display info (label and color classes)
function getChangeRatingInfo(rating) {
  const ratings = {
    significant_positive: {
      label: 'Great Progress',
      bgClass: 'bg-status-success/20',
      textClass: 'text-status-success',
    },
    moderate: {
      label: 'Good Progress',
      bgClass: 'bg-status-info/20',
      textClass: 'text-status-info',
    },
    minimal: {
      label: 'Steady',
      bgClass: 'bg-dial-elevated',
      textClass: 'text-indices-medium',
    },
    concerning: {
      label: 'Needs Attention',
      bgClass: 'bg-status-warning/20',
      textClass: 'text-status-warning',
    },
  }
  return ratings[rating] || null
}

function MediaGallerySection({ clientId }) {
  const [expanded, setExpanded] = useState(false)

  const { data: mediaData, isLoading } = useQuery({
    queryKey: ['clientMedia', clientId],
    queryFn: () => clients.getMedia(clientId),
    enabled: expanded, // Only fetch when expanded
  })

  return (
    <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
      >
        <h3 className="font-semibold text-indices-bright font-display">
          Media Gallery
          {mediaData?.count != null && (
            <span className="ml-2 text-sm font-normal text-indices-muted">({mediaData.count})</span>
          )}
        </h3>
        <svg className={`w-5 h-5 text-indices-muted transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="mt-3">
          <MediaGallery media={mediaData?.media || []} isLoading={isLoading} />
        </div>
      )}
    </div>
  )
}

function PhotoComparisonModal({ photos, basePath, onClose }) {
  const [beforeId, setBeforeId] = useState(photos.length > 1 ? photos[photos.length - 1].id : null)
  const [afterId, setAfterId] = useState(photos[0]?.id || null)

  const beforePhoto = photos.find(p => p.id === beforeId)
  const afterPhoto = photos.find(p => p.id === afterId)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div className="w-full max-w-2xl bg-dial-surface border border-dial-border rounded-xl overflow-hidden shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-dial-border">
          <h3 className="text-lg font-semibold text-indices-bright font-display">Progress Comparison</h3>
          <button onClick={onClose} className="p-1 text-indices-muted hover:text-indices-bright">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Photo selectors */}
        <div className="p-4">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs text-indices-muted mb-1 block">Before</label>
              <select value={beforeId || ''} onChange={e => setBeforeId(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm bg-dial-black border border-dial-border rounded-lg text-lume-teal focus:border-lume-teal focus:outline-none">
                {photos.map(p => <option key={p.id} value={p.id}>{formatDate(p.photoDate)} — {p.photoTypeDisplay || p.photoType}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-indices-muted mb-1 block">After</label>
              <select value={afterId || ''} onChange={e => setAfterId(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm bg-dial-black border border-dial-border rounded-lg text-lume-teal focus:border-lume-teal focus:outline-none">
                {photos.map(p => <option key={p.id} value={p.id}>{formatDate(p.photoDate)} — {p.photoTypeDisplay || p.photoType}</option>)}
              </select>
            </div>
          </div>

          {/* Side by side photos */}
          {beforePhoto && afterPhoto && (
            <div className="grid grid-cols-2 gap-2">
              <div className="relative">
                <img src={`${basePath}/wa/progressPhoto?id=${beforePhoto.id}`} alt="Before progress photo" className="w-full aspect-[3/4] object-cover rounded-lg" />
                <div className="absolute bottom-2 left-2 right-2 bg-black/70 backdrop-blur-sm rounded-md p-2 text-center">
                  <div className="text-[10px] text-indices-muted font-mono tracking-widest">BEFORE</div>
                  <div className="text-xs text-indices-bright">{formatDate(beforePhoto.photoDate)}</div>
                </div>
              </div>
              <div className="relative">
                <img src={`${basePath}/wa/progressPhoto?id=${afterPhoto.id}`} alt="After progress photo" className="w-full aspect-[3/4] object-cover rounded-lg" />
                <div className="absolute bottom-2 left-2 right-2 bg-black/70 backdrop-blur-sm rounded-md p-2 text-center">
                  <div className="text-[10px] text-lume-teal font-mono tracking-widest">AFTER</div>
                  <div className="text-xs text-indices-bright">{formatDate(afterPhoto.photoDate)}</div>
                </div>
              </div>
            </div>
          )}

          {/* AI analysis if available */}
          {afterPhoto?.aiSummary && (
            <div className="mt-3 p-3 bg-dial-elevated border border-dial-border rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-purple-400 font-medium">AI Analysis</span>
                {afterPhoto.changeRatingDisplay && (
                  <span className="text-xs text-lume-teal">{afterPhoto.changeRatingDisplay}</span>
                )}
              </div>
              <p className="text-sm text-indices-medium">{afterPhoto.aiSummary}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function PhotosSection({ clientId }) {
  const basePath = getBasePath()
  const [expandedPhoto, setExpandedPhoto] = useState(null)
  const [showComparison, setShowComparison] = useState(false)

  const { data: photos, isLoading } = useQuery({
    queryKey: ['clientPhotos', clientId],
    queryFn: () => clients.getPhotos(clientId),
  })

  if (isLoading) {
    return (
      <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
        <h3 className="font-semibold text-indices-bright mb-3 font-display">Progress Photos</h3>
        <div className="grid grid-cols-4 gap-2">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="aspect-square rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  const photoList = photos?.photos || []

  if (photoList.length === 0) {
    return (
      <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
        <h3 className="font-semibold text-indices-bright mb-3 font-display">Progress Photos</h3>
        <p className="text-indices-muted text-sm text-center py-4">No photos yet</p>
      </div>
    )
  }

  // Find the most recent photo with AI analysis
  const latestAnalyzedPhoto = photoList.find(p => p.aiSummary || p.aiAnalysis)

  // Show only the 4 most recent photos
  const recentPhotos = photoList.slice(0, 4)

  return (
    <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-indices-bright font-display">Progress Photos</h3>
        {photoList.length > 4 && (
          <span className="text-sm text-indices-muted">+{photoList.length - 4} more</span>
        )}
      </div>

      {/* Latest AI Analysis Summary */}
      {latestAnalyzedPhoto && (
        <div className="mb-3 p-3 bg-gradient-to-r from-purple-500/10 to-lume-teal/10 border border-purple-500/20 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-dial-elevated border border-dial-border rounded-lg shadow-sm flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-purple-400">AI Analysis</span>
                {getChangeRatingInfo(latestAnalyzedPhoto.changeRating) && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getChangeRatingInfo(latestAnalyzedPhoto.changeRating).bgClass} ${getChangeRatingInfo(latestAnalyzedPhoto.changeRating).textClass}`}>
                    {getChangeRatingInfo(latestAnalyzedPhoto.changeRating).label}
                  </span>
                )}
              </div>
              <p className="text-sm text-indices-medium line-clamp-2">
                {latestAnalyzedPhoto.aiSummary || 'Analysis available'}
              </p>
              {latestAnalyzedPhoto.aiAnalysis && (
                <button
                  onClick={() => setExpandedPhoto(expandedPhoto === latestAnalyzedPhoto.id ? null : latestAnalyzedPhoto.id)}
                  className="text-xs text-purple-400 font-medium mt-1 hover:text-purple-300 transition-colors"
                >
                  {expandedPhoto === latestAnalyzedPhoto.id ? 'Show less' : 'Read full analysis'}
                </button>
              )}
            </div>
          </div>

          {/* Expanded analysis */}
          {expandedPhoto === latestAnalyzedPhoto.id && latestAnalyzedPhoto.aiAnalysis && (
            <div className="mt-3 pt-3 border-t border-purple-500/20">
              <p className="text-sm text-indices-medium whitespace-pre-wrap">
                {latestAnalyzedPhoto.aiAnalysis}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Photo grid */}
      <div className="grid grid-cols-4 gap-2">
        {recentPhotos.map(photo => {
          const ratingInfo = getChangeRatingInfo(photo.changeRating)
          return (
            <div key={photo.id} className="relative aspect-square bg-dial-elevated rounded-lg overflow-hidden group">
              <img
                src={`${basePath}/wa/progressPhoto?id=${photo.id}`}
                alt={photo.photoType || 'Progress'}
                className="w-full h-full object-cover"
              />
              {/* Small indicator for analyzed photos */}
              {photo.aiSummary && ratingInfo && (
                <div className={`absolute bottom-1 right-1 w-3 h-3 rounded-full ${ratingInfo.bgClass} border border-dial-black shadow-sm`} />
              )}
              {/* "Analyzing..." overlay for new photos without analysis */}
              {!photo.aiSummary && !photo.aiAnalysis && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-white text-[10px] font-medium bg-black/60 px-1.5 py-0.5 rounded">
                    Analyzing...
                  </span>
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className="mt-3 pt-3 border-t border-dial-border flex items-center justify-between">
        <p className="text-xs text-indices-muted">
          {photoList.length} total • Latest: {formatDate(recentPhotos[0]?.photoDate)}
        </p>
        {photoList.length >= 2 && (
          <button
            onClick={() => setShowComparison(true)}
            className="text-xs text-lume-teal font-medium hover:text-lume-green transition-colors flex items-center gap-1"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
            Compare
          </button>
        )}
      </div>

      {showComparison && (
        <PhotoComparisonModal photos={photoList} basePath={basePath} onClose={() => setShowComparison(false)} />
      )}
    </div>
  )
}

// Memory type icon and color mapping - Panerai dark theme
const memoryTypeConfig = {
  medical: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
    color: 'text-status-error bg-status-error/20',
    label: 'Medical',
  },
  preference: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ),
    color: 'text-purple-400 bg-purple-500/20',
    label: 'Preference',
  },
  personal: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    color: 'text-status-info bg-status-info/20',
    label: 'Personal',
  },
  goal: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'text-status-success bg-status-success/20',
    label: 'Goal',
  },
  behavioral: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    color: 'text-orange-400 bg-orange-500/20',
    label: 'Behavioral',
  },
  situational: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'text-indices-muted bg-dial-elevated',
    label: 'Situational',
  },
}

function EditMemoryModal({ memory, onClose, onSave }) {
  const [content, setContent] = useState(memory.content)
  const [context, setContext] = useState(memory.context || '')
  const [memoryType, setMemoryType] = useState(memory.memoryType)
  const [isCritical, setIsCritical] = useState(memory.isCritical)
  const [saving, setSaving] = useState(false)

  const typeOptions = [
    { value: 'medical', label: 'Medical' },
    { value: 'preference', label: 'Preference' },
    { value: 'personal', label: 'Personal' },
    { value: 'goal', label: 'Goal' },
    { value: 'behavioral', label: 'Behavioral' },
    { value: 'situational', label: 'Situational' },
  ]

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave({ id: memory.id, content, context: context || null, memoryType, isCritical })
      onClose()
    } catch {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="w-full max-w-lg bg-dial-surface border border-dial-border rounded-xl p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-indices-bright mb-4">Edit Memory</h3>

        <label className="block text-xs text-indices-muted mb-1">Type</label>
        <select
          value={memoryType}
          onChange={e => setMemoryType(e.target.value)}
          className="w-full px-3 py-2 mb-3 text-sm bg-dial-black border border-dial-border rounded-lg text-lume-teal focus:border-lume-teal focus:outline-none"
        >
          {typeOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>

        <label className="block text-xs text-indices-muted mb-1">Content</label>
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 mb-3 text-sm bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:border-lume-teal focus:outline-none resize-none"
        />

        <label className="block text-xs text-indices-muted mb-1">Context (optional)</label>
        <input
          type="text"
          value={context}
          onChange={e => setContext(e.target.value)}
          placeholder="Why this memory matters..."
          className="w-full px-3 py-2 mb-3 text-sm bg-dial-black border border-dial-border rounded-lg text-indices-bright placeholder-indices-recessed focus:border-lume-teal focus:outline-none"
        />

        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input type="checkbox" checked={isCritical} onChange={e => setIsCritical(e.target.checked)} className="accent-red-500" />
          <span className="text-sm text-indices-muted">Critical (safety-important, prevents decay)</span>
        </label>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-indices-muted hover:text-indices-bright transition-colors">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !content.trim()}
            className="px-4 py-2 text-sm bg-lume-teal text-dial-black rounded-lg font-medium hover:bg-lume-green transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

function MemoryCard({ memory, onPin, onDelete, onEdit }) {
  const config = memoryTypeConfig[memory.memoryType] || memoryTypeConfig.personal
  const strengthPercent = Math.round(memory.strength * 100)

  return (
    <div className={`p-3 rounded-lg border ${memory.isCritical ? 'border-status-error/50 bg-status-error/10' : 'border-dial-border bg-dial-elevated'}`}>
      <div className="flex items-start gap-2">
        <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${config.color}`}>
          {config.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-xs font-medium text-indices-muted">{config.label}</span>
            {memory.isCritical && (
              <span className="px-1.5 py-0.5 bg-status-error/20 text-status-error rounded text-[10px] font-medium">
                CRITICAL
              </span>
            )}
            {memory.isPinned && !memory.isCritical && (
              <svg className="w-3 h-3 text-lume-cream" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
            )}
          </div>
          <p className="text-sm text-indices-bright">{memory.content}</p>
          {memory.context && (
            <p className="text-xs text-indices-muted mt-1 italic">{memory.context}</p>
          )}
          <div className="flex items-center gap-3 mt-2">
            <div className="flex items-center gap-1">
              <div className="w-16 h-1.5 bg-dial-border rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${strengthPercent > 70 ? 'bg-status-success' : strengthPercent > 40 ? 'bg-status-warning' : 'bg-status-error'}`}
                  style={{ width: `${strengthPercent}%` }}
                />
              </div>
              <span className="text-[10px] text-indices-recessed">{strengthPercent}%</span>
            </div>
            {memory.reinforcementCount > 0 && (
              <span className="text-[10px] text-indices-recessed">
                Reinforced {memory.reinforcementCount}x
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <button
            onClick={() => onEdit(memory)}
            className="p-1 text-indices-recessed hover:text-lume-teal rounded transition-colors"
            title="Edit"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => onPin(memory)}
            className={`p-1 rounded ${memory.isPinned ? 'text-lume-cream' : 'text-indices-recessed hover:text-lume-cream'} transition-colors`}
            title={memory.isPinned ? 'Unpin' : 'Pin'}
          >
            <svg className="w-4 h-4" fill={memory.isPinned ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
          </button>
          <button
            onClick={() => onDelete(memory)}
            className="p-1 text-indices-recessed hover:text-status-error rounded transition-colors"
            title="Archive"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

function MemoriesSection({ clientId }) {
  const queryClient = useQueryClient()
  const [showAll, setShowAll] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [editingMemory, setEditingMemory] = useState(null)

  const { data: memoriesData, isLoading } = useQuery({
    queryKey: ['clientMemories', clientId, showArchived],
    queryFn: () => memories.list(clientId, { includeArchived: showArchived || undefined }),
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['clientMemories', clientId] })
  }

  const updateMutation = useMutation({
    mutationFn: ({ id, ...updates }) => memories.update(id, updates),
    onSuccess: invalidate,
  })

  const pinMutation = useMutation({
    mutationFn: (memory) => memories.update(memory.id, { isPinned: !memory.isPinned }),
    onSuccess: invalidate,
  })

  const deleteMutation = useMutation({
    mutationFn: (memory) => memories.delete(memory.id),
    onSuccess: invalidate,
  })

  const restoreMutation = useMutation({
    mutationFn: (memory) => memories.restore(memory.id),
    onSuccess: invalidate,
  })

  if (isLoading) {
    return (
      <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
        <h3 className="font-semibold text-indices-bright mb-3 font-display">AI Memory</h3>
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  const memoryList = memoriesData?.memories || []

  if (memoryList.length === 0) {
    return (
      <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
        <h3 className="font-semibold text-indices-bright mb-3 font-display">AI Memory</h3>
        <div className="text-center py-6">
          <div className="w-12 h-12 bg-dial-elevated border border-dial-border rounded-full flex items-center justify-center mx-auto mb-2">
            <svg className="w-6 h-6 text-indices-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <p className="text-indices-muted text-sm">No memories yet</p>
          <p className="text-indices-recessed text-xs mt-1">The AI will learn about this client during chats</p>
        </div>
      </div>
    )
  }

  // Split active vs archived
  const activeMemories = memoryList.filter(m => m.status !== 'archived')
  const archivedMemories = memoryList.filter(m => m.status === 'archived')

  // Sort memories: critical first, then pinned, then by strength
  const sortedMemories = useMemo(() => [...activeMemories].sort((a, b) => {
    if (a.isCritical !== b.isCritical) return b.isCritical ? 1 : -1
    if (a.isPinned !== b.isPinned) return b.isPinned ? 1 : -1
    return b.strength - a.strength
  }), [activeMemories])

  const displayedMemories = showAll ? sortedMemories : sortedMemories.slice(0, 5)
  const criticalCount = activeMemories.filter(m => m.isCritical).length

  return (
    <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-indices-bright font-display">AI Memory</h3>
          {criticalCount > 0 && (
            <span className="px-1.5 py-0.5 bg-status-error/20 text-status-error rounded text-[10px] font-medium">
              {criticalCount} critical
            </span>
          )}
        </div>
        <span className="text-sm text-indices-muted">{activeMemories.length} memories</span>
      </div>

      <div className="space-y-2">
        {displayedMemories.map(memory => (
          <MemoryCard
            key={memory.id}
            memory={memory}
            onEdit={setEditingMemory}
            onPin={(m) => pinMutation.mutate(m)}
            onDelete={(m) => {
              if (window.confirm('Archive this memory? It can be restored later.')) {
                deleteMutation.mutate(m)
              }
            }}
          />
        ))}
      </div>

      {activeMemories.length > 5 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full mt-3 py-2 text-sm text-lume-teal font-medium hover:text-lume-green transition-colors"
        >
          {showAll ? 'Show less' : `Show all ${activeMemories.length} memories`}
        </button>
      )}

      {/* Archived toggle */}
      <div className="mt-3 pt-3 border-t border-dial-border flex items-center justify-between">
        <p className="text-xs text-indices-recessed">
          Memories decay over time
        </p>
        <button
          onClick={() => setShowArchived(!showArchived)}
          className={`text-xs transition-colors ${showArchived ? 'text-lume-teal' : 'text-indices-recessed hover:text-lume-teal'}`}
        >
          {showArchived ? 'Hide archived' : 'Show archived'}
        </button>
      </div>

      {/* Archived memories */}
      {showArchived && archivedMemories.length > 0 && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-indices-muted mb-1">{archivedMemories.length} archived</p>
          {archivedMemories.map(memory => (
            <div key={memory.id} className="p-3 rounded-lg border border-dial-border bg-dial-elevated opacity-60">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-xs text-indices-muted">
                      {(memoryTypeConfig[memory.memoryType] || memoryTypeConfig.personal).label}
                    </span>
                    <span className="text-[10px] text-indices-recessed px-1 py-0.5 rounded bg-dial-surface">Archived</span>
                  </div>
                  <p className="text-sm text-indices-muted">{memory.content}</p>
                </div>
                <button
                  onClick={() => restoreMutation.mutate(memory)}
                  className="p-1 text-indices-recessed hover:text-lume-teal rounded transition-colors flex-shrink-0"
                  title="Restore"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editingMemory && (
        <EditMemoryModal
          memory={editingMemory}
          onClose={() => setEditingMemory(null)}
          onSave={(updates) => updateMutation.mutateAsync(updates)}
        />
      )}
    </div>
  )
}

const NOTE_TYPE_CONFIG = {
  general: { label: 'General', color: 'text-indices-medium bg-dial-elevated' },
  behavioral: { label: 'Behavioral', color: 'text-orange-400 bg-orange-500/20' },
  progress: { label: 'Progress', color: 'text-status-success bg-status-success/20' },
  injury: { label: 'Injury', color: 'text-status-error bg-status-error/20' },
  preference: { label: 'Preference', color: 'text-purple-400 bg-purple-500/20' },
  goal: { label: 'Goal', color: 'text-status-info bg-status-info/20' },
  concern: { label: 'Concern', color: 'text-status-warning bg-status-warning/20' },
}

function CoachingNotesSection({ clientId }) {
  const queryClient = useQueryClient()
  const [showAll, setShowAll] = useState(false)
  const [showAddNote, setShowAddNote] = useState(false)
  const [newNoteContent, setNewNoteContent] = useState('')
  const [newNoteType, setNewNoteType] = useState('general')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data: notesData, isLoading } = useQuery({
    queryKey: ['coachingNotes', clientId],
    queryFn: () => coachingNotes.list(clientId),
  })

  const deactivateMutation = useMutation({
    mutationFn: (noteId) => coachingNotes.deactivate(noteId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['coachingNotes', clientId] }),
  })

  const handleAddNote = async (e) => {
    e.preventDefault()
    if (!newNoteContent.trim()) return
    setIsSubmitting(true)
    try {
      await coachingNotes.create(clientId, newNoteContent.trim(), newNoteType)
      queryClient.invalidateQueries({ queryKey: ['coachingNotes', clientId] })
      setNewNoteContent('')
      setNewNoteType('general')
      setShowAddNote(false)
    } catch (error) {
      console.error('Failed to add note:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
        <h3 className="font-semibold text-indices-bright mb-3 font-display">Coaching Notes</h3>
        <div className="space-y-2">
          {[1, 2].map(i => <Skeleton key={i} className="h-14 rounded-lg" />)}
        </div>
      </div>
    )
  }

  const notesList = notesData?.notes || notesData || []

  return (
    <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-indices-bright font-display">Coaching Notes</h3>
        <button
          onClick={() => setShowAddNote(!showAddNote)}
          className="text-sm text-lume-teal font-medium hover:text-lume-green transition-colors"
        >
          {showAddNote ? 'Cancel' : '+ Add Note'}
        </button>
      </div>

      {showAddNote && (
        <form onSubmit={handleAddNote} className="mb-3 p-3 bg-dial-elevated border border-dial-border rounded-lg space-y-2">
          <select
            value={newNoteType}
            onChange={(e) => setNewNoteType(e.target.value)}
            className="w-full px-2 py-1.5 bg-dial-surface border border-dial-border rounded-lg text-sm text-indices-bright"
          >
            {Object.entries(NOTE_TYPE_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>{config.label}</option>
            ))}
          </select>
          <textarea
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
            placeholder="Add a coaching note..."
            rows={2}
            className="w-full px-2 py-1.5 bg-dial-surface border border-dial-border rounded-lg text-sm text-indices-bright placeholder-indices-muted resize-none"
          />
          <button
            type="submit"
            disabled={isSubmitting || !newNoteContent.trim()}
            className="w-full py-2 bg-lume-teal text-dial-black rounded-lg text-sm font-medium hover:bg-lume-green disabled:opacity-50 transition-colors"
          >
            {isSubmitting ? 'Saving...' : 'Save Note'}
          </button>
        </form>
      )}

      {notesList.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-indices-muted text-sm">No coaching notes yet</p>
          <p className="text-indices-recessed text-xs mt-1">AI-generated notes appear here during coaching chats</p>
        </div>
      ) : (
        <div className="space-y-2">
          {(showAll ? notesList : notesList.slice(0, 5)).map(note => {
            const config = NOTE_TYPE_CONFIG[note.noteType] || NOTE_TYPE_CONFIG.general
            return (
              <div key={note.id} className="p-3 bg-dial-elevated border border-dial-border rounded-lg">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${config.color}`}>
                        {config.label}
                      </span>
                      {note.source === 'ai_generated' && (
                        <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded text-[10px] font-medium">AI</span>
                      )}
                    </div>
                    <p className="text-sm text-indices-bright">{note.content}</p>
                    <p className="text-[10px] text-indices-recessed mt-1">{formatDate(note.createdAt)}</p>
                  </div>
                  <button
                    onClick={() => {
                      if (window.confirm('Deactivate this note?')) {
                        deactivateMutation.mutate(note.id)
                      }
                    }}
                    className="p-1 text-indices-recessed hover:text-status-error rounded transition-colors flex-shrink-0"
                    title="Deactivate"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {notesList.length > 5 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full mt-3 py-2 text-sm text-lume-teal font-medium hover:text-lume-green transition-colors"
        >
          {showAll ? 'Show less' : `Show all ${notesList.length} notes`}
        </button>
      )}
    </div>
  )
}

function WorkoutLogSection({ clientId }) {
  const [showAll, setShowAll] = useState(false)

  const { data: logsData, isLoading } = useQuery({
    queryKey: ['workoutLogs', clientId],
    queryFn: () => workoutLogs.list(clientId, 30),
  })

  if (isLoading) {
    return (
      <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
        <h3 className="font-semibold text-indices-bright mb-3 font-display">Recent Workouts</h3>
        <div className="space-y-2">
          {[1, 2].map(i => <Skeleton key={i} className="h-14 rounded-lg" />)}
        </div>
      </div>
    )
  }

  const logs = logsData?.logs || logsData || []

  return (
    <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-indices-bright font-display">Recent Workouts</h3>
        <span className="text-sm text-indices-muted">Last 30 days</span>
      </div>

      {logs.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-indices-muted text-sm">No workout logs yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {(showAll ? logs : logs.slice(0, 5)).map((log, idx) => (
            <div key={log.id || idx} className="p-3 bg-dial-elevated border border-dial-border rounded-lg cursor-pointer hover:border-lume-teal/40 transition-colors" onClick={() => log.workoutTemplateId && (window.location.href = `/workouts/${log.workoutTemplateId}`)}>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-indices-bright">{log.workoutName || 'Workout'}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-indices-muted">{formatDate(log.completedDate || log.startedAt)}</span>
                    {log.durationMinutes && (
                      <span className="text-xs text-indices-recessed">{log.durationMinutes} min</span>
                    )}
                  </div>
                </div>
                {log.overallRpe && (
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    log.overallRpe <= 5 ? 'bg-status-success/20 text-status-success' :
                    log.overallRpe <= 7 ? 'bg-status-warning/20 text-status-warning' :
                    'bg-status-error/20 text-status-error'
                  }`}>
                    RPE {log.overallRpe}
                  </span>
                )}
              </div>
              {log.exercises && log.exercises.length > 0 && (
                <div className="mt-2 pt-2 border-t border-dial-border">
                  <div className="flex flex-wrap gap-1">
                    {log.exercises.slice(0, 4).map((ex, i) => (
                      <span key={i} className="text-[10px] text-indices-recessed bg-dial-surface px-1.5 py-0.5 rounded">
                        {ex.exerciseName || ex.name}
                      </span>
                    ))}
                    {log.exercises.length > 4 && (
                      <span className="text-[10px] text-indices-recessed">+{log.exercises.length - 4} more</span>
                    )}
                  </div>
                </div>
              )}
              {log.overallNotes && (
                <p className="text-xs text-indices-muted mt-1 italic">{log.overallNotes}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {logs.length > 5 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full mt-3 py-2 text-sm text-lume-teal font-medium hover:text-lume-green transition-colors"
        >
          {showAll ? 'Show less' : `Show all ${logs.length} workouts`}
        </button>
      )}
    </div>
  )
}

function AssignedWorkoutsSection({ clientId }) {
  const [showAll, setShowAll] = useState(false)
  const queryClient = useQueryClient()

  const { data: assignData, isLoading } = useQuery({
    queryKey: ['assignments', clientId],
    queryFn: () => assignments.list(clientId, 60),
  })

  const markMissed = useMutation({
    mutationFn: ({ id, undo }) => assignments.markMissed(id, undo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignments', clientId] })
    },
  })

  if (isLoading) {
    return (
      <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
        <h3 className="font-semibold text-indices-bright mb-3 font-display">Assigned Workouts</h3>
        <div className="space-y-2">
          {[1, 2].map(i => <Skeleton key={i} className="h-14 rounded-lg" />)}
        </div>
      </div>
    )
  }

  const items = assignData?.assignments || []

  if (items.length === 0) return null

  const statusColor = (effectiveStatus) => {
    switch (effectiveStatus) {
      case 'completed': return 'bg-status-success/20 text-status-success'
      case 'missed': return 'bg-status-error/20 text-status-error'
      case 'scheduled': return 'bg-status-info/20 text-status-info'
      case 'in_progress': return 'bg-status-warning/20 text-status-warning'
      case 'skipped': return 'bg-indices-muted/20 text-indices-muted'
      case 'cancelled': return 'bg-indices-muted/20 text-indices-muted'
      default: return 'bg-indices-muted/20 text-indices-muted'
    }
  }

  const statusLabel = (effectiveStatus) => {
    switch (effectiveStatus) {
      case 'completed': return 'Completed'
      case 'missed': return 'Missed'
      case 'scheduled': return 'Upcoming'
      case 'in_progress': return 'In Progress'
      case 'skipped': return 'Skipped'
      case 'cancelled': return 'Cancelled'
      default: return effectiveStatus
    }
  }

  return (
    <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-indices-bright font-display">Assigned Workouts</h3>
        <span className="text-sm text-indices-muted">Last 60 days</span>
      </div>

      <div className="space-y-2">
        {(showAll ? items : items.slice(0, 5)).map((aw) => {
          const es = aw.effectiveStatus
          const isMissed = es === 'missed'
          const isPast = aw.effectiveStatus === 'completed' || aw.effectiveStatus === 'missed'

          return (
            <div key={aw.id} className="p-3 bg-dial-elevated border border-dial-border rounded-lg cursor-pointer hover:border-lume-teal/40 transition-colors" onClick={() => aw.workoutTemplateId && (window.location.href = `/workouts/${aw.workoutTemplateId}`)}>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-indices-bright">{aw.workoutName || 'Workout'}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-indices-muted">{formatDate(aw.scheduledDate)}</span>
                    {aw.notes && (
                      <span className="text-xs text-indices-recessed italic truncate max-w-[140px]">{aw.notes}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor(es)}`}>
                    {statusLabel(es)}
                  </span>
                  {isPast && (
                    <button
                      onClick={() => markMissed.mutate({ id: aw.id, undo: isMissed })}
                      disabled={markMissed.isPending}
                      className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                        isMissed
                          ? 'bg-status-success/20 text-status-success hover:bg-status-success/30'
                          : 'bg-status-error/20 text-status-error hover:bg-status-error/30'
                      }`}
                      title={isMissed ? 'Undo missed — mark as completed' : 'Mark as missed'}
                    >
                      {isMissed ? 'Undo' : 'Missed'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {items.length > 5 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full mt-3 py-2 text-sm text-lume-teal font-medium hover:text-lume-green transition-colors"
        >
          {showAll ? 'Show less' : `Show all ${items.length} workouts`}
        </button>
      )}
    </div>
  )
}

function QuickStatsSection({ clientId }) {
  const { data: statsList } = useQuery({
    queryKey: ['clientStats', clientId],
    queryFn: () => clientStats.list(clientId),
  })

  const latest = statsList?.[0]
  if (!latest) return null

  return (
    <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-indices-bright font-display">Stats Snapshot</h3>
        <Link to={`/clients/${clientId}/stats`} className="text-sm text-lume-teal font-medium hover:text-lume-green transition-colors">
          View All
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {latest.weightKg && (
          <div className="text-center p-2 bg-dial-elevated border border-dial-border rounded-lg">
            <p className="text-lg font-semibold text-lume-teal font-display">{latest.weightKg}</p>
            <p className="text-xs text-indices-muted">kg</p>
          </div>
        )}
        {latest.bodyFatPercent && (
          <div className="text-center p-2 bg-dial-elevated border border-dial-border rounded-lg">
            <p className="text-lg font-semibold text-lume-teal font-display">{latest.bodyFatPercent}%</p>
            <p className="text-xs text-indices-muted">body fat</p>
          </div>
        )}
        {latest.chestCm && (
          <div className="text-center p-2 bg-dial-elevated border border-dial-border rounded-lg">
            <p className="text-lg font-semibold text-lume-teal font-display">{latest.chestCm}</p>
            <p className="text-xs text-indices-muted">chest cm</p>
          </div>
        )}
        {latest.waistCm && (
          <div className="text-center p-2 bg-dial-elevated border border-dial-border rounded-lg">
            <p className="text-lg font-semibold text-lume-teal font-display">{latest.waistCm}</p>
            <p className="text-xs text-indices-muted">waist cm</p>
          </div>
        )}
        {latest.hipsCm && (
          <div className="text-center p-2 bg-dial-elevated border border-dial-border rounded-lg">
            <p className="text-lg font-semibold text-lume-teal font-display">{latest.hipsCm}</p>
            <p className="text-xs text-indices-muted">hips cm</p>
          </div>
        )}
      </div>
      <p className="text-[10px] text-indices-recessed text-center mt-2">
        Recorded {formatDate(latest.recordedDate || latest.recordedAt)}
      </p>
    </div>
  )
}

const READINESS_COLORS = {
  great: 'text-green-400',
  good: 'text-lume-teal',
  fair: 'text-yellow-400',
  poor: 'text-red-400',
}

function RecoverySection({ clientId }) {
  const { data } = useQuery({
    queryKey: ['clientRecovery', clientId],
    queryFn: () => clientRecovery.list(clientId, 14),
  })

  if (!data?.logs?.length) return null

  const logs = data.logs
  const colorClass = READINESS_COLORS[data.readinessLabel] || 'text-indices-muted'

  return (
    <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-indices-bright font-display">Recovery</h3>
        {data.avgReadiness > 0 && (
          <div className={`text-sm font-bold ${colorClass} capitalize`}>
            {data.avgReadiness}/5 {data.readinessLabel}
          </div>
        )}
      </div>

      {/* Mini trend bars */}
      <div className="flex items-end gap-1 h-12 mb-3">
        {[...logs].reverse().map((log) => {
          const barColor = READINESS_COLORS[log.readinessLabel] || 'text-indices-muted'
          const bgColor = barColor.replace('text-', 'bg-')
          const heightPct = log.readinessScore > 0 ? Math.max(15, (log.readinessScore / 5) * 100) : 10
          return (
            <div
              key={log.id}
              className={`flex-1 rounded-t ${bgColor}`}
              style={{ height: `${heightPct}%`, opacity: 0.75 }}
              title={`${log.recordedDate?.split('T')[0]}: ${log.readinessScore}/5`}
            />
          )
        })}
      </div>

      {/* Latest entries */}
      <div className="space-y-1.5">
        {logs.slice(0, 3).map((log) => {
          const date = new Date(log.recordedDate)
          const dayStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
          const logColor = READINESS_COLORS[log.readinessLabel] || 'text-indices-muted'
          return (
            <div key={log.id} className="flex items-center justify-between text-xs">
              <span className="text-indices-medium">{dayStr}</span>
              <div className="flex items-center gap-3 text-indices-muted">
                {log.sleepHours != null && <span>{log.sleepHours}h</span>}
                {log.sleepQuality != null && <span>sleep {log.sleepQuality}/5</span>}
                {log.muscleSoreness != null && <span>sore {log.muscleSoreness}/5</span>}
                {log.energyLevel != null && <span>energy {log.energyLevel}/5</span>}
                <span className={`font-semibold capitalize ${logColor}`}>{log.readinessLabel}</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ClientDirectivesSection({ clientId }) {
  const [showAdd, setShowAdd] = useState(false)
  const [newDirective, setNewDirective] = useState('')
  const [newCategory, setNewCategory] = useState('general')
  const queryClient = useQueryClient()

  const { data: directivesData } = useQuery({
    queryKey: ['clientDirectives', clientId],
    queryFn: () => clientDirectives.list(clientId),
  })

  const createMutation = useMutation({
    mutationFn: () => clientDirectives.create(parseInt(clientId), newDirective, newCategory),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientDirectives', clientId] })
      setNewDirective('')
      setNewCategory('general')
      setShowAdd(false)
    },
  })

  const directives = directivesData?.directives || directivesData || []

  return (
    <div className="bg-dial-surface border border-dial-border rounded-xl">
      <div className="px-4 py-3 border-b border-dial-border flex items-center justify-between">
        <h3 className="font-semibold text-indices-bright font-display">AI Directives</h3>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="text-sm text-lume-teal hover:opacity-80 font-medium"
        >
          {showAdd ? 'Cancel' : '+ Add'}
        </button>
      </div>

      {showAdd && (
        <div className="p-4 border-b border-dial-border space-y-3">
          <textarea
            value={newDirective}
            onChange={(e) => setNewDirective(e.target.value)}
            placeholder="e.g., 'Never recommend keto' or 'Always ask about knee pain before leg exercises'"
            rows={3}
            className="w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal text-sm"
          />
          <div className="flex gap-3">
            <select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="flex-1 px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal text-sm"
            >
              <option value="general">General</option>
              <option value="nutrition">Nutrition</option>
              <option value="training">Training</option>
              <option value="communication">Communication</option>
              <option value="medical">Medical/Safety</option>
            </select>
            <button
              onClick={() => createMutation.mutate()}
              disabled={!newDirective.trim() || createMutation.isPending}
              className="px-4 py-2 bg-lume-teal text-dial-black rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Adding...' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {directives.length > 0 ? (
        <div className="divide-y divide-dial-border">
          {directives.map((d, i) => (
            <div key={d.id || i} className="px-4 py-3">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <p className="text-sm text-indices-bright">{d.directive}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs px-1.5 py-0.5 bg-dial-elevated text-indices-muted rounded border border-dial-border capitalize">
                      {d.category || 'general'}
                    </span>
                    {d.createdAt && (
                      <span className="text-xs text-indices-muted">{formatDate(d.createdAt)}</span>
                    )}
                    {d.expiresAt && (
                      <span className="text-xs text-orange-400">Expires: {formatDate(d.expiresAt)}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-6 text-center">
          <p className="text-indices-muted text-sm">No client-specific directives</p>
          <p className="text-indices-recessed text-xs mt-1">Add directives to customize AI behavior for this client</p>
        </div>
      )}
    </div>
  )
}

function BillingSection({ clientId }) {
  const { data: packagesData } = useQuery({
    queryKey: ['billingPackages', { clientId }],
    queryFn: () => billing.list({ clientId }),
  })

  const packages = packagesData?.packages || packagesData || []
  const packageList = Array.isArray(packages) ? packages : []

  if (packageList.length === 0) return null

  const activePackages = packageList.filter(p => p.status === 'draft' || p.status === 'invoiced' || p.status === 'paid')
  const outstandingPackages = packageList.filter(p => p.status === 'invoiced')
  const outstandingTotal = outstandingPackages.reduce((sum, p) => {
    const price = typeof p.price === 'number' ? p.price : 0
    return sum + price
  }, 0)

  const displayPackage = activePackages[0]

  function formatCurrency(val) {
    if (val == null) return '$0.00'
    const dollars = typeof val === 'number' && val > 100 ? val / 100 : val
    return `$${Number(dollars).toFixed(2)}`
  }

  return (
    <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-indices-bright font-display">Billing</h3>
        <Link
          to={`/billing?clientId=${clientId}`}
          className="text-sm text-lume-teal font-medium hover:text-lume-green transition-colors"
        >
          View All
        </Link>
      </div>

      {displayPackage && (
        <Link
          to={`/billing/${displayPackage.id}`}
          className="block bg-dial-elevated border border-dial-border rounded-lg p-3 hover:border-lume-teal/50 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-indices-bright truncate">{displayPackage.name}</p>
              <p className="text-xs text-indices-muted mt-0.5">
                {displayPackage.packageType === 'per_session'
                  ? `${displayPackage.sessionsUsed || 0}/${displayPackage.sessionCount || 0} sessions`
                  : displayPackage.packageType?.replace(/_/g, ' ')
                }
              </p>
            </div>
            <div className="text-right flex-shrink-0 ml-3">
              <p className="text-sm font-semibold text-lume-teal font-display">{formatCurrency(displayPackage.price)}</p>
              <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                displayPackage.status === 'paid' ? 'bg-lume-teal/20 text-lume-teal' :
                displayPackage.status === 'invoiced' ? 'bg-status-warning/20 text-status-warning' :
                displayPackage.status === 'cancelled' ? 'bg-status-error/20 text-status-error' :
                'bg-dial-elevated text-indices-muted border border-dial-border'
              }`}>
                {displayPackage.status === 'invoiced' ? 'Outstanding' : displayPackage.status}
              </span>
            </div>
          </div>

          {displayPackage.packageType === 'per_session' && displayPackage.sessionCount > 0 && (
            <div className="mt-2">
              <div className="w-full h-1.5 bg-dial-black rounded-full overflow-hidden">
                <div
                  className="h-full bg-lume-teal rounded-full transition-all"
                  style={{ width: `${Math.min(100, ((displayPackage.sessionsUsed || 0) / displayPackage.sessionCount) * 100)}%` }}
                />
              </div>
            </div>
          )}
        </Link>
      )}

      {outstandingTotal > 0 && (
        <div className="mt-3 flex items-center justify-between text-sm">
          <span className="text-indices-muted">Outstanding balance</span>
          <span className="font-semibold text-status-warning">{formatCurrency(outstandingTotal)}</span>
        </div>
      )}
    </div>
  )
}

const DIRECTIVE_CATEGORY_CONFIG = {
  general: { label: 'General', color: 'text-indices-medium bg-dial-elevated' },
  nutrition: { label: 'Nutrition', color: 'text-status-success bg-status-success/20' },
  training: { label: 'Training', color: 'text-status-info bg-status-info/20' },
  medical: { label: 'Medical', color: 'text-status-error bg-status-error/20' },
  personality: { label: 'Personality', color: 'text-purple-400 bg-purple-500/20' },
}

function AIDirectivesSection({ clientId }) {
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newDirective, setNewDirective] = useState('')
  const [newCategory, setNewCategory] = useState('general')
  const [deletingId, setDeletingId] = useState(null)

  const { data: directivesData, isLoading } = useQuery({
    queryKey: ['clientDirectives', clientId],
    queryFn: () => clientDirectives.list(clientId),
    enabled: expanded,
  })

  const createMutation = useMutation({
    mutationFn: ({ directive, category }) => clientDirectives.create(clientId, directive, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientDirectives', clientId] })
      setNewDirective('')
      setNewCategory('general')
      setShowAddForm(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => directives.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientDirectives', clientId] })
      setDeletingId(null)
    },
  })

  const handleCreate = (e) => {
    e.preventDefault()
    if (!newDirective.trim()) return
    createMutation.mutate({ directive: newDirective.trim(), category: newCategory })
  }

  const handleDelete = (directiveId) => {
    if (deletingId === directiveId) {
      deleteMutation.mutate(directiveId)
    } else {
      setDeletingId(directiveId)
      setTimeout(() => setDeletingId((cur) => (cur === directiveId ? null : cur)), 3000)
    }
  }

  const allDirectives = directivesData?.directives || []
  const clientSpecific = allDirectives.filter(d => d.isClientSpecific || d.clientId)
  const globalOnes = allDirectives.filter(d => !d.isClientSpecific && !d.clientId)
  const totalCount = allDirectives.length

  return (
    <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-indices-bright font-display">AI Directives</h3>
          {expanded && totalCount > 0 && (
            <span className="px-1.5 py-0.5 bg-lume-teal/20 text-lume-teal rounded text-[10px] font-medium">
              {totalCount}
            </span>
          )}
        </div>
        <svg className={`w-5 h-5 text-indices-muted transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2].map(i => <Skeleton key={i} className="h-14 rounded-lg" />)}
            </div>
          ) : (
            <>
              {/* Client-specific directives */}
              {clientSpecific.length > 0 && (
                <div className="space-y-2">
                  {clientSpecific.map(d => {
                    const catConfig = DIRECTIVE_CATEGORY_CONFIG[d.category] || DIRECTIVE_CATEGORY_CONFIG.general
                    return (
                      <div key={d.id} className="p-3 bg-dial-elevated border-l-2 border-lume-teal rounded-lg">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${catConfig.color}`}>
                                {catConfig.label}
                              </span>
                            </div>
                            <p className="text-sm text-indices-bright">{d.directive}</p>
                            {d.expiresAt && (
                              <p className="text-[10px] text-indices-recessed mt-1">Expires {formatDate(d.expiresAt)}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleDelete(d.id)}
                            className={`p-1 rounded transition-colors flex-shrink-0 ${
                              deletingId === d.id
                                ? 'text-status-error'
                                : 'text-indices-recessed hover:text-status-error'
                            }`}
                            title={deletingId === d.id ? 'Click again to confirm' : 'Delete directive'}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Global directives */}
              {globalOnes.length > 0 && (
                <div className="space-y-2">
                  {clientSpecific.length > 0 && (
                    <div className="flex items-center gap-2 pt-1">
                      <div className="flex-1 h-px bg-dial-border" />
                      <span className="text-[10px] text-indices-recessed font-mono tracking-wider">GLOBAL</span>
                      <div className="flex-1 h-px bg-dial-border" />
                    </div>
                  )}
                  {globalOnes.map(d => {
                    const catConfig = DIRECTIVE_CATEGORY_CONFIG[d.category] || DIRECTIVE_CATEGORY_CONFIG.general
                    return (
                      <div key={d.id} className="p-3 bg-dial-elevated border border-dial-border rounded-lg opacity-60">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${catConfig.color}`}>
                                {catConfig.label}
                              </span>
                              <span className="px-1.5 py-0.5 bg-dial-surface text-indices-recessed rounded text-[10px] font-medium">
                                GLOBAL
                              </span>
                            </div>
                            <p className="text-sm text-indices-medium">{d.directive}</p>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {allDirectives.length === 0 && !showAddForm && (
                <div className="text-center py-4">
                  <p className="text-indices-muted text-sm">No directives for this client</p>
                  <p className="text-indices-recessed text-xs mt-1">Add directives to customize AI behavior</p>
                </div>
              )}

              {/* Add directive form */}
              {showAddForm ? (
                <form onSubmit={handleCreate} className="p-3 bg-dial-elevated border border-dial-border rounded-lg space-y-2">
                  <select
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="w-full px-2 py-1.5 bg-dial-surface border border-dial-border rounded-lg text-sm text-indices-bright"
                  >
                    {Object.entries(DIRECTIVE_CATEGORY_CONFIG).map(([value, config]) => (
                      <option key={value} value={value}>{config.label}</option>
                    ))}
                  </select>
                  <textarea
                    value={newDirective}
                    onChange={(e) => setNewDirective(e.target.value)}
                    placeholder="e.g. Always suggest low-impact alternatives for knee exercises..."
                    rows={3}
                    className="w-full px-2 py-1.5 bg-dial-surface border border-dial-border rounded-lg text-sm text-indices-bright placeholder-indices-muted resize-none"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setShowAddForm(false); setNewDirective(''); setNewCategory('general') }}
                      className="flex-1 py-2 text-sm text-indices-muted hover:text-indices-bright transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createMutation.isPending || !newDirective.trim()}
                      className="flex-1 py-2 bg-lume-teal text-dial-black rounded-lg text-sm font-medium hover:bg-lume-green disabled:opacity-50 transition-colors"
                    >
                      {createMutation.isPending ? 'Saving...' : 'Save Directive'}
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="w-full py-2 text-sm text-lume-teal font-medium hover:text-lume-green transition-colors flex items-center justify-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Directive
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default function ClientDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [showPortalLinkModal, setShowPortalLinkModal] = useState(false)
  const [portalLink, setPortalLink] = useState(null)
  const [inviteStatus, setInviteStatus] = useState(null)
  const [showQuickMessage, setShowQuickMessage] = useState(false)

  const { data: client, isLoading } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clients.get(id),
  })

  const generateLinkMutation = useMutation({
    mutationFn: () => clients.generatePortalLink(id),
    onSuccess: (data) => {
      if (data.success && data.portalLink) {
        setPortalLink(data.portalLink)
      }
    },
  })

  const handleSendInvite = async (method) => {
    setInviteStatus({ sending: method })
    try {
      const result = await clients.sendPortalInvite(id, method)
      if (result.success) {
        setInviteStatus({ success: true, message: `Invite sent via ${method === 'sms' ? 'SMS' : 'email'}!` })
      } else {
        setInviteStatus({ success: false, message: result.error || 'Failed to send invite' })
      }
    } catch (e) {
      setInviteStatus({ success: false, message: e.message || 'Failed to send invite' })
    }
  }

  const queryClient = useQueryClient()

  const archiveMutation = useMutation({
    mutationFn: (makeActive) => clients.update(id, { isActive: makeActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', id] })
      queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
  })

  const handleArchive = () => {
    if (window.confirm(`Archive ${client.firstName} ${client.lastName}? They'll be hidden from your active roster.`)) {
      archiveMutation.mutate(false)
    }
  }

  const handleReactivate = () => {
    archiveMutation.mutate(true)
  }

  const resetOnboardingMutation = useMutation({
    mutationFn: () => clients.resetOnboarding(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', id] })
    },
  })

  const handleResetOnboarding = () => {
    if (window.confirm('Reset onboarding for this client? They will see the intake questionnaire again next time they open the portal.')) {
      resetOnboardingMutation.mutate()
    }
  }

  const handleOpenPortalLink = () => {
    setPortalLink(null)
    setInviteStatus(null)
    setShowPortalLinkModal(true)
  }

  if (isLoading) {
    return (
      <PageLayout>
        <PageHeader title="Client" showBack backTo="/clients" />
        <div className="p-4">
          <Skeleton className="h-48 rounded-xl" />
        </div>
      </PageLayout>
    )
  }

  if (!client) {
    return (
      <PageLayout>
        <PageHeader title="Client" showBack backTo="/clients" />
        <div className="p-4 text-center py-12">
          <p className="text-indices-muted">Client not found</p>
        </div>
      </PageLayout>
    )
  }

  const initials = `${client.firstName?.[0] || ''}${client.lastName?.[0] || ''}`.toUpperCase()

  return (
    <PageLayout>
      <PageHeader
        title="Client"
        showBack
        backTo="/clients"
        actions={
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowQuickMessage(true)}
              className="p-2 text-lume-teal hover:text-lume-green transition-colors"
              title="Send Message"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </button>
            <button
              onClick={() => {
                clients.exportProgressReport(id).catch(err => {
                  console.error('Failed to export report:', err)
                  alert('Failed to generate progress report')
                })
              }}
              className="p-2 text-lume-teal hover:text-lume-green transition-colors"
              title="Export Progress Report"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
            <Link
              to={`/clients/${id}/edit`}
              className="p-2 text-lume-teal hover:text-lume-green transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </Link>
          </div>
        }
      />

      <div className="px-4 py-4 space-y-4">
        {/* Profile Card */}
        <div className="bg-dial-surface border border-dial-border rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-lume-teal/20 text-lume-teal rounded-full flex items-center justify-center text-xl font-semibold font-display">
              {initials}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-indices-bright font-display">
                {client.firstName} {client.lastName}
              </h2>
              <p className="text-indices-muted">{client.email}</p>
              {client.phone && (
                <p className="text-indices-recessed text-sm">{client.phone}</p>
              )}
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              client.isActive
                ? 'bg-status-success/20 text-status-success'
                : 'bg-dial-elevated text-indices-muted'
            }`}>
              {client.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>

          <div className="mt-4 pt-4 border-t border-dial-border grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-indices-muted">Training Type</p>
              <p className="font-medium text-indices-bright capitalize">{client.trainingType?.replace('_', ' ') || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-indices-muted">Member Since</p>
              <p className="font-medium text-indices-bright">{formatDate(client.createdAt) || '-'}</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <h3 className="text-sm font-medium text-indices-muted px-1 mb-2 font-display">Quick Actions</h3>
          <div className="grid grid-cols-4 gap-3">
            <QuickActionButton
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              }
              label="Message"
              color="teal"
              onClick={() => setShowQuickMessage(true)}
            />
            <QuickActionLink
              to={`/clients/${id}/stats`}
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              }
              label="Stats"
              color="blue"
            />
            <QuickActionButton
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              }
              label="Photos"
              color="purple"
              onClick={() => navigate(`/clients/${id}/stats`)}
            />
            <QuickActionButton
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              }
              label="Workouts"
              color="green"
              onClick={() => navigate('/workouts')}
            />
          </div>
        </div>

        {/* Goals */}
        {client.goals && (
          <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
            <h3 className="font-semibold text-indices-bright mb-2 font-display">Goals</h3>
            <p className="text-indices-medium text-sm whitespace-pre-wrap">{client.goals}</p>
          </div>
        )}

        {/* Stats Snapshot */}
        <QuickStatsSection clientId={id} />

        {/* Recovery / Readiness */}
        <RecoverySection clientId={id} />

        {/* Recent Workouts */}
        <WorkoutLogSection clientId={id} />

        {/* Assigned Workouts (with mark missed) */}
        <AssignedWorkoutsSection clientId={id} />

        {/* Coaching Notes */}
        <CoachingNotesSection clientId={id} />

        {/* Recent Photos */}
        <PhotosSection clientId={id} />

        {/* Media Gallery */}
        <MediaGallerySection clientId={id} />

        {/* AI Memory */}
        <MemoriesSection clientId={id} />

        {/* Per-Client AI Directives */}
        <ClientDirectivesSection clientId={id} />

        {/* Billing */}
        <BillingSection clientId={id} />

        {/* AI Directives */}
        <AIDirectivesSection clientId={id} />

        {/* More Actions */}
        <div className="bg-dial-surface border border-dial-border rounded-xl divide-y divide-dial-border">
          <Link
            to={`/client-chats?clientId=${id}`}
            className="w-full p-4 text-left flex items-center justify-between hover:bg-dial-elevated transition-colors"
          >
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-lume-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
              </svg>
              <span className="text-indices-bright">View AI Chats</span>
            </div>
            <svg className="w-5 h-5 text-indices-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
          <button
            onClick={handleOpenPortalLink}
            className="w-full p-4 text-left flex items-center justify-between hover:bg-dial-elevated transition-colors"
          >
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-lume-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <span className="text-indices-bright">Send Portal Invite</span>
            </div>
            <svg className="w-5 h-5 text-indices-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={handleResetOnboarding}
            disabled={resetOnboardingMutation.isPending}
            className="w-full p-4 text-left flex items-center justify-between hover:bg-dial-elevated transition-colors disabled:opacity-50"
          >
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-lume-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="text-indices-bright">
                {resetOnboardingMutation.isPending ? 'Resetting...' : 'Reset Onboarding'}
              </span>
            </div>
            {resetOnboardingMutation.isSuccess && (
              <span className="text-xs text-lume-teal">Done</span>
            )}
          </button>
        </div>

        {/* Archive / Reactivate */}
        <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
          <h3 className="text-sm font-medium text-indices-muted mb-3 font-display">Client Status</h3>
          {client.isActive !== false ? (
            <button
              onClick={handleArchive}
              disabled={archiveMutation.isPending}
              className="w-full flex items-center justify-center gap-2 py-3 bg-dial-elevated border border-dial-border rounded-xl text-indices-medium hover:text-status-error hover:border-status-error/30 transition-colors disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
              <span className="font-medium">
                {archiveMutation.isPending ? 'Archiving...' : 'Archive Client'}
              </span>
            </button>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 px-3 py-2 bg-dial-elevated border border-dial-border rounded-lg">
                <svg className="w-4 h-4 text-indices-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                <span className="text-sm text-indices-muted">This client is archived</span>
              </div>
              <button
                onClick={handleReactivate}
                disabled={archiveMutation.isPending}
                className="w-full flex items-center justify-center gap-2 py-3 bg-lume-teal text-dial-black rounded-xl font-medium hover:bg-lume-green hover:shadow-lume transition-all disabled:opacity-50"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>{archiveMutation.isPending ? 'Reactivating...' : 'Reactivate Client'}</span>
              </button>
            </div>
          )}
          {archiveMutation.isSuccess && (
            <p className="text-sm text-lume-teal text-center mt-2">
              {client.isActive !== false ? 'Client is now active' : 'Client has been archived'}
            </p>
          )}
        </div>
      </div>

      <PortalLinkModal
        isOpen={showPortalLinkModal}
        onClose={() => setShowPortalLinkModal(false)}
        client={client}
        onGenerate={() => generateLinkMutation.mutate()}
        link={portalLink}
        isGenerating={generateLinkMutation.isPending}
        onSendInvite={handleSendInvite}
        inviteStatus={inviteStatus}
      />

      <QuickMessageModal
        isOpen={showQuickMessage}
        onClose={() => setShowQuickMessage(false)}
        client={client}
      />
    </PageLayout>
  )
}
